import { Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyDto, LanguageDto, MobileNumberDto, TimezoneDto } from '@workspace/data-types';
import {
  AlertSnackBarService,
  AuraInternationalizationCurrencyEditComponent,
  AuraInternationalizationLanguageEditComponent,
  AuraInternationalizationTimezoneEditComponent,
} from '@workspace/ng-aura';
import { UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { tap } from 'rxjs';

@Component({
  selector: 'core-user-internationalization',
  templateUrl: './user-internationalization.component.html',
  imports: [
    AuraInternationalizationLanguageEditComponent,
    AuraInternationalizationTimezoneEditComponent,
    AuraInternationalizationCurrencyEditComponent,
  ],
  providers: [],
})
export class UserInternationalizationCoreComponent {
  private destroyRef = inject(DestroyRef);
  private userStore = inject(UserStore);
  private api = inject(ApiService);
  private alertSnackbar = inject(AlertSnackBarService);

  title = input<string>();

  isLoading = true;
  currentUser = this.userStore.currentUser;
  userMobileNumber = signal<MobileNumberDto | null>(null);

  closeIdentityEditing = signal<boolean>(false);
  closeContactEmailEditing = signal<boolean>(false);
  closeContactPhoneEditing = signal<boolean>(false);

  constructor() {
    this.currentUser();
    this.isLoading = false;

    effect(() => {
      const mobileNumber = this.currentUser()?.contactInfos?.mobileNumber;
      if (mobileNumber) {
        this.userMobileNumber.set({ mobileNumber });
      } else {
        this.userMobileNumber.set(null);
      }
    });
  }

  saveLanguage(language: LanguageDto) {
    this.api
      .user()
      .settings()
      .internationalization()
      .updateLanguage(language)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((userIdentity: LanguageDto) => {
          if (userIdentity) {
            this.userStore.updateCurrentUserLanguage(language);
            this.alertSnackbar.success({
              title: 'Modification du langage',
              message: 'Le langage de votre compte a été modifié avec succès',
            });
            this.closeIdentityEditing.set(true);
          }
        }),
      )
      .subscribe();
    this.closeIdentityEditing.set(false);
  }

  saveTimezone(timezone: TimezoneDto) {
    this.api
      .user()
      .settings()
      .internationalization()
      .updateTimezone(timezone)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((userIdentity: TimezoneDto) => {
          if (userIdentity) {
            this.userStore.updateCurrentUserTimezone(timezone);
            this.alertSnackbar.success({
              title: 'Modification du fuseau horraire',
              message: 'Le fuseau horraire de votre compte a été modifié avec succès',
            });
            this.closeIdentityEditing.set(true);
          }
        }),
      )
      .subscribe();
    this.closeIdentityEditing.set(false);
  }

  saveCurrency(currency: CurrencyDto) {
    this.api
      .user()
      .settings()
      .internationalization()
      .updateCurrency(currency)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((currency: CurrencyDto) => {
          if (currency) {
            this.userStore.updateCurrentUserCurrency(currency);
            this.alertSnackbar.success({
              title: 'Modification de votre devise',
              message: 'Le devise de votre compte a été modifié avec succès',
            });
            this.closeIdentityEditing.set(true);
          }
        }),
      )
      .subscribe();
    this.closeIdentityEditing.set(false);
  }
}
